var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import AppState from '../../AppState';
import { FibreProviderTypes, OnePlatformOptionServiceTypeEnum, OnePlatformForecastAccessTechnologyEnum, } from '../../models/enums';
import { getMappedAccessType, planDisplayToggle, } from '../helperFunctions';
import onePlatformSendToSalesFunnel from './onePlatformSendToSalesFunnel';
import onePlatformBuildPlanDisplay from './onePlatformBuildPlanDisplay';
import onePlatformGetNodePlanData from './onePlatformGetNodePlanData';
import onePlatformQuoteUpdate from './onePlatformQuoteUpdate';
import onePlatformGetPromoByLocid from './onePlatformGetPromoByLocid';
import onePlatformCheckPromoValidity from './onePlatformCheckPromoValidity';
import { pricingStrategy, onePlatformAssemblePromoData } from './onePlatformAssemblePromoData';
import onePlatformStampPromo from './onePlatformStampPromo';
import onePlatformApiError from './onePlatformApiError';
/**
 * Retrieves discount information from the given Discount object.
 *
 * @param {PlanData} selectedPlanData - The data of the selected plan.
 * @param {Discount} discount - The Discount object containing discount information.
 * @returns {DiscountInfo} - An object containing the extracted discount information.
 */
const getDiscount = (selectedPlanData, discount) => {
    var _a, _b, _c, _d, _e, _f, _g;
    let uptoPeriod = (_a = discount.quoteItemPrice[0].price.attributes.find((attribute) => attribute.name === 'upToPeriods')) === null || _a === void 0 ? void 0 : _a.value;
    if (typeof uptoPeriod !== 'undefined') {
        uptoPeriod = parseInt(uptoPeriod);
    }
    else {
        uptoPeriod = null;
    }
    let value = (_c = (_b = discount.quoteItemPrice[0]) === null || _b === void 0 ? void 0 : _b.price) === null || _c === void 0 ? void 0 : _c.value;
    if (typeof value !== 'undefined') {
        value = Math.abs(discount.quoteItemPrice[0].price.value).toString();
    }
    else {
        value = 0;
    }
    let actionType = (_d = discount.quoteItemPrice[0].price.attributes.find((attribute) => attribute.name === 'Discount_Type')) === null || _d === void 0 ? void 0 : _d.value;
    if (typeof actionType === 'undefined') {
        actionType = null;
    }
    let percentage = (_e = discount.quoteItemPrice[0].price.attributes.find((attribute) => attribute.name === 'percentage')) === null || _e === void 0 ? void 0 : _e.value;
    if (typeof percentage === 'undefined') {
        percentage = null;
    }
    let promotionName = (_f = discount.quoteItemPrice[0].price.attributes.find((attribute) => attribute.name === 'promotionName')) === null || _f === void 0 ? void 0 : _f.value;
    if (typeof promotionName === 'undefined') {
        promotionName = null;
    }
    const data = {
        name: discount.name,
        discountType: discount.discountType.toLowerCase(),
        value: value,
        upToPeriod: uptoPeriod,
        terminationDate: discount.terminationDate,
        actionType: actionType,
        percentage: percentage,
        description: (_g = discount.digitalUIMarketing[0]) === null || _g === void 0 ? void 0 : _g.productName,
        promotionName: promotionName,
        elementGuid: discount.elementGuid,
        parentElementGuid: discount.parentElementGuid,
    };
    const promoAction = {
        actionObjectId: '',
        actionType: data.actionType,
        actionValue: data.value,
        type: data.actionType,
    };
    const actualPrice = selectedPlanData.data[0].attributes.field_plan_monthly_fee;
    return Object.assign(Object.assign({}, data), pricingStrategy(promoAction, actualPrice, data.upToPeriod));
};
/**
 * Retrieves the available discount of a given discount type for a plan.
 *
 * @param {Plan} plan - The plan object to check for available discounts.
 * @param {PlanData} selectedPlanData - The data of the selected plan.
 * @param {string} discountType - The type of discount to search for.
 * @returns {DiscountInfo | boolean} The available discount object of the specified type, or false if no discount is found.
 */
const onePlatformGetAvailableDiscount = (plan, selectedPlanData, discountType) => {
    let promos = plan.discounts.filter((discount) => discount.discountType.toLowerCase() === discountType);
    if (promos.length > 0) {
        return getDiscount(selectedPlanData, promos[0]);
    }
    return false;
};
/**
 * Calculates the mass market discount for a given plan on one platform.
 *
 * @param {Plan} plan - The plan for which to calculate the mass market discount.
 * @param {PlanData} selectedPlanData - The data of the selected plan.
 * @returns {DiscountInfo | boolean} The mass market discount for the specified plan.
 */
const onePlatformGetMassMarketDiscount = (plan, selectedPlanData) => onePlatformGetAvailableDiscount(plan, selectedPlanData, 'global');
/**
 * Calculates the speed boost discount for a given plan.
 *
 * @param {Plan} plan - The plan object for which to calculate the discount.
 * @param {PlanData} selectedPlanData - The data of the selected plan.
 * @returns {DiscountInfo | boolean} The speed boost discount for the given plan.
 */
const onePlatformGetSpeedBoostDiscount = (plan, selectedPlanData) => onePlatformGetAvailableDiscount(plan, selectedPlanData, 'speed_boost');
/**
 * Determines if there is a plan available for the entered address. Triggers
 * "Service Unavailable" block or continues with journey.
 *
 * @param {OnePlatformSQ} serviceQualificationObject
 *   The service address object obtained via One Platform.
 * @param {Quote} quote
 *   The quote object obtained via One Platform.
 * @param {ProductOfferingObject} productOffering
 *   The product offering object.
 * @param {string} actionObjectId
 *   The action object ID of the selected plan.
 */
const onePlatformGetServicesAvailable = (serviceQualificationObject, quote, productOffering, actionObjectId) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d, _e;
    if (serviceQualificationObject.serviceQualificationItem[0].qualificationResult === 'qualified') {
        const drupalJsObjects = AppState.get('drupalJsObjects');
        const addressConfirmed = AppState.get('addressConfirmed');
        const addressCheckProgress = AppState.get('addressCheckProgress');
        const planId = AppState.get('planId');
        const planType = AppState.get('planType');
        const { Drupal, Window, context, settings } = drupalJsObjects;
        const { token } = settings;
        const serviceAvailable = context.querySelector('#service-available');
        const serviceSelected = context.querySelector('#service-selected');
        const selectDifferentPlanLink = context.querySelector('#select-different');
        const messageWrapper = context.querySelector('#message-wrapper');
        const confirmationBlock = context.querySelector('#address-confirmation-4');
        const accessType = serviceQualificationObject.serviceQualificationItem[0].service.characteristic.find((item) => item.name === 'accessType').value;
        // Check if FoD ready.
        const fodReadyPlans = ['SBCHRG9', 'SBCHRG11'];
        let isFodReady = false;
        if (accessType === OnePlatformOptionServiceTypeEnum.FTTN || accessType === OnePlatformOptionServiceTypeEnum.FTTC) {
            const technologyPlan = serviceQualificationObject.serviceQualificationItem[0].service.supportingResource.find((item) => item.name === 'TechnologyPlan');
            const forecastAccessTech = (_a = technologyPlan === null || technologyPlan === void 0 ? void 0 : technologyPlan.characteristic.find((item) => item.name === 'ForecastAccessTechnology')) === null || _a === void 0 ? void 0 : _a.value;
            const RTCDate = (_b = technologyPlan === null || technologyPlan === void 0 ? void 0 : technologyPlan.characteristic.find((item) => item.name === 'RTCDate')) === null || _b === void 0 ? void 0 : _b.value;
            if (forecastAccessTech &&
                (forecastAccessTech === OnePlatformForecastAccessTechnologyEnum.FIBRE) &&
                RTCDate && (new Date(RTCDate) <= new Date())) {
                isFodReady = true;
            }
        }
        const selectedPlanNext = context.querySelector('#address-check-next');
        serviceSelected.style.marginBottom = '2rem';
        messageWrapper.style.display = 'block';
        selectedPlanNext.style.display = 'block';
        context.querySelector('#plan-selection-title').style.display = 'none';
        // Display plan badges.
        let badges = [];
        let promoCode;
        // Get mass market discounts.
        const nbnPlans = quote.ui.filter((uiItem) => uiItem.type && uiItem.type.toLowerCase() === 'data_subscription_specification');
        let selectedPlan = nbnPlans.find((plan) => plan.quoteItemPrice[0].price.attributes.find((attribute) => attribute.name === 'Recurring_RPC_ID' &&
            attribute.value === actionObjectId));
        const selectedPlanPrice = (_e = (_d = (_c = selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.quoteItemPrice) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.price) === null || _e === void 0 ? void 0 : _e.value;
        // Gather data around other available plans.
        const availablePlans = nbnPlans.filter((plan) => {
            var _a, _b, _c, _d;
            return (_d = (_c = (_b = (_a = plan.quoteItemPrice) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.price) === null || _c === void 0 ? void 0 : _c.attributes) === null || _d === void 0 ? void 0 : _d.find((attribute) => attribute.name === 'Recurring_RPC_ID' &&
                attribute.value !== actionObjectId);
        });
        const availablePlansRpcIDs = availablePlans.flatMap((plan) => {
            var _a, _b, _c;
            return (_c = (_b = (_a = plan.quoteItemPrice) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.price) === null || _c === void 0 ? void 0 : _c.attributes.find((attribute) => attribute.name === 'Recurring_RPC_ID');
        });
        /**
         * Extracts valid attributes from the given data.
         *
         * @param {any} data - The data to extract attributes from.
         * @returns {Array|null} - An array of valid attributes, or null if no valid attributes are found.
         */
        const extractValidAttributes = (data) => Array.isArray(data)
            ? data[0]['attributes']
            : typeof data === 'object'
                ? data['attributes']
                : null;
        /**
         * Retrieves the value of a query parameter from the current URL's query string.
         *
         * @param {string} param - The name of the query parameter.
         * @returns {string | null} - The value of the query parameter, or null if it is not found.
         */
        const getQueryParamValue = (param) => {
            const queryParams = new URLSearchParams(Window.location.search);
            return queryParams.get(param);
        };
        /**
         * Retrieves the promo code from the affiliate's query parameters.
         *
         * @returns {string | null} The promo code if found, null otherwise.
         */
        const getPromoCodeFromAffiliate = () => {
            const promoCode = getQueryParamValue('promo_code');
            if (promoCode !== null && promoCode.length > 0) {
                return promoCode;
            }
            return null;
        };
        /**
         * Retrieves the promo code based on location ID.
         *
         * @param {OnePlatformData} data - The data object containing the service qualification item with location ID.
         * @returns {Promise<string | null>} - A promise that resolves to the promo code or null if no promo code is found.
         */
        const getPromoCodeByLocid = (data) => __awaiter(void 0, void 0, void 0, function* () {
            const promoObject = yield onePlatformGetPromoByLocid(data.serviceQualificationItem[0].service.place[0].id);
            if ((promoObject === null || promoObject === void 0 ? void 0 : promoObject.length) > 0) {
                const { action } = promoObject[0];
                const [{ actionValue }] = action;
                return actionValue;
            }
            return null;
        });
        /**
         * Retrieves valid promo data for the given promo code.
         *
         * @param {string} promoCode - The promo code to check validity.
         * @returns {Promise<RetrievedPromoData | null>} - The retrieved promo data, if valid. Otherwise, null.
         */
        const getValidPromoData = (promoCode, selectedPlanData) => __awaiter(void 0, void 0, void 0, function* () {
            const validPromoData = yield onePlatformCheckPromoValidity(promoCode);
            AppState.set('onePlatformData.validPromoData', validPromoData);
            AppState.set('onePlatformData.promoCode', promoCode);
            if (validPromoData && typeof validPromoData !== 'boolean') {
                return onePlatformAssemblePromoData(validPromoData, selectedPlanData);
            }
            return null;
        });
        /**
         * Handles promotions based on the given promoCode, plan, badges, and selectedPlanData.
         *
         * @param {string} promoCode - The promo code to be used for the promotion.
         * @param {Plan} plan - The current plan.
         * @param {string[]} badges - The array of badges to be updated with new promotions.
         * @param {PlanData} selectedPlanData - The data of the selected plan.
         * @returns {Promise<DiscountInfo | boolean>} - The selected promotion or false if no promotion is applied.
         */
        const handlePromotions = (promoCode, plan, badges, selectedPlanData) => __awaiter(void 0, void 0, void 0, function* () {
            if (promoCode && promoCode !== 'undefined') {
                const promoData = yield getValidPromoData(promoCode, selectedPlanData);
                if (promoData) {
                    badges.push(promoData.promo_tile);
                    selectedPlanData.data[0].attributes.field_plan_monthly_fee_discount = promoData.promo_price.toString();
                }
            }
            else {
                let massMarketPromo = onePlatformGetMassMarketDiscount(plan, selectedPlanData);
                let speedBoostPromo = onePlatformGetSpeedBoostDiscount(plan, selectedPlanData);
                if (speedBoostPromo && typeof speedBoostPromo !== 'boolean') {
                    if (typeof speedBoostPromo.promo_tile !== 'undefined') {
                        badges.push(speedBoostPromo.promo_tile);
                    }
                    return speedBoostPromo;
                }
                else if (massMarketPromo && typeof massMarketPromo !== 'boolean') {
                    if (typeof massMarketPromo.promo_tile !== 'undefined') {
                        badges.push(massMarketPromo.promo_tile);
                    }
                    return massMarketPromo;
                }
            }
            return false;
        });
        /**
         * Handles the click event on the selected plan next button.
         * @param {MouseEvent} event - The click event object.
         * @returns {Promise<void>} - A promise that resolves when the action is completed.
         */
        const selectedPlanNextOnClick = (event) => __awaiter(void 0, void 0, void 0, function* () {
            event.preventDefault();
            selectedPlanNext.disabled = true;
            selectedPlanNext.querySelector('.spinner').classList.remove('invisible');
            // Extract selected plan details
            const selectedPlanElem = context.querySelector('.service-plans--service-plan.selected');
            if (selectedPlanElem === null) {
                return;
            }
            const selectedActionObjectId = selectedPlanElem.getAttribute('data-plan-one-platform-id');
            // Obtain the selected plan based on the selected actionObjectId
            const selectedPlan = nbnPlans.find((plan) => plan.quoteItemPrice[0].price.attributes.some((attribute) => attribute.name === 'Recurring_RPC_ID' && attribute.value === selectedActionObjectId));
            let promotions = [];
            let selectedPlanData = yield onePlatformGetNodePlanData(selectedActionObjectId, planType);
            let massMarketPromo = onePlatformGetMassMarketDiscount(selectedPlan, selectedPlanData);
            if (massMarketPromo) {
                promotions.push(massMarketPromo);
            }
            let speedBoostPromo = onePlatformGetSpeedBoostDiscount(selectedPlan, selectedPlanData);
            if (speedBoostPromo) {
                promotions.push(speedBoostPromo);
            }
            try {
                const validPromoData = AppState.get('onePlatformData.validPromoData');
                const isPromoCodeAndValidDataExists = promoCode && validPromoData && typeof validPromoData !== 'boolean';
                if (isPromoCodeAndValidDataExists) {
                    promotions = false;
                }
                const quoteUpdated = yield onePlatformQuoteUpdate(quote, productOffering, selectedActionObjectId, promotions);
                const isQuoteUpdated = quoteUpdated && typeof quoteUpdated === 'object';
                if (isQuoteUpdated) {
                    yield stampPromoAndSendToFunnel(quote, selectedActionObjectId);
                }
            }
            catch (error) {
                onePlatformApiError();
                console.error('Error:', error);
            }
        });
        // Selected plan is available.
        if (typeof selectedPlan !== 'undefined') {
            badges.push(Drupal.t('Available'));
            confirmationBlock.appendChild(addressConfirmed);
            confirmationBlock.style.display = 'block';
            context.querySelector('#package-confirmation-message').innerText = `Great! ${getMappedAccessType(accessType)} is available.`;
            context.querySelector('#plan-name-1').innerText = FibreProviderTypes[1];
            // Display service confirmation availability.
            addressCheckProgress.style.display = 'none';
            serviceAvailable.classList.add('p-lg-4', 'address-tile');
            serviceAvailable.querySelector('.service-available--text').style.display = 'block';
            context.querySelector('#service-available-icon').style.display = 'block';
            messageWrapper.classList.add('service-available--congratulations');
            if (isFodReady && fodReadyPlans.includes(planId)) {
                badges.push(Drupal.t('Free Fibre upgrade'));
            }
            // Update more info & learn more modals with matching Plan details.
            serviceSelected.innerHTML = '';
            let selectedPlanData = yield onePlatformGetNodePlanData(actionObjectId, planType);
            const onePlatformData = AppState.get('onePlatformData');
            const affiliatePromoCode = getPromoCodeFromAffiliate();
            promoCode = affiliatePromoCode !== null ? affiliatePromoCode : yield getPromoCodeByLocid(onePlatformData);
            const discount = yield handlePromotions(promoCode, selectedPlan, badges, selectedPlanData);
            let priceDiscount = (typeof discount !== 'boolean') ? discount.promo_price : 0;
            onePlatformBuildPlanDisplay(actionObjectId, selectedPlanData, badges, 0, priceDiscount, discount ? discount : false, serviceSelected);
            if (!promoCode && discount && typeof discount !== 'boolean') {
                let discountValue = parseInt(discount.value);
                if (discountValue > 0) {
                    const currentCostElem = serviceSelected.querySelector('.cost');
                    let discountElem = serviceSelected.querySelector('.discount');
                    if (discountElem === null) {
                        discountElem = document.createElement('div');
                        discountElem.classList.add('discount');
                        currentCostElem.insertAdjacentElement('beforebegin', discountElem);
                    }
                    discountElem.innerHTML = `Was <span>${selectedPlanPrice}</span>/mth`;
                    currentCostElem.innerHTML = `<span>$${selectedPlanPrice - discountValue}</span>/mth`;
                }
            }
            if (serviceAvailable) {
                selectDifferentPlanLink.style.display = 'none';
                if (!isFodReady) {
                    planDisplayToggle(serviceAvailable);
                }
            }
            /**
             * Asynchronously process the available plans.
             *
             * @async
             * @function processPlans
             * @returns {Promise<void>} - A promise that resolves when the processing is complete.
             */
            const processPlans = () => __awaiter(void 0, void 0, void 0, function* () {
                for (const [i, availablePlan] of availablePlansRpcIDs.entries()) {
                    const planData = yield onePlatformGetNodePlanData(availablePlan.value, planType, accessType);
                    const responseData = planData['data'];
                    const attributes = responseData && extractValidAttributes(responseData);
                    if (!attributes) {
                        continue;
                    }
                    const availablePlanId = attributes.field_plan_id;
                    const badges = [Drupal.t('Available')];
                    if (isFodReady && (parseInt(attributes.field_plan_monthly_fee, 10) < selectedPlanPrice)) {
                        continue;
                    }
                    const affiliatePromoCode = getPromoCodeFromAffiliate();
                    let promoCode;
                    promoCode = affiliatePromoCode !== null ? affiliatePromoCode : yield getPromoCodeByLocid(onePlatformData);
                    const discount = yield handlePromotions(promoCode, availablePlans[i], badges, planData);
                    let priceDiscount = (typeof discount !== 'boolean') ? discount.promo_price : 0;
                    if (isFodReady && fodReadyPlans.includes(availablePlanId)) {
                        badges.push(Drupal.t('Free Fibre upgrade'));
                    }
                    onePlatformBuildPlanDisplay(availablePlanId, planData, badges, 0, priceDiscount, discount ? discount : false, null);
                }
            });
            processPlans().then(() => {
                if (!isFodReady) {
                    return;
                }
                const upgradeAvailability = context.querySelector('#upgrade-availability');
                const upgradeMessages = context.querySelector('#upgrade-messages');
                if (planId !== 'SBCHRG11') {
                    upgradeMessages.innerHTML = `
          <p class="great-news--title">${Drupal.t('Get free fibre upgrade')}</p>
          <p class="great-news--text">${Drupal.t('Great news, your address qualifies for a complementary upgrade to Fibre to the Premise (FTTP) on selected plans. Find out more about <a href="#" data-bs-target="#nbn-upgrades" data-bs-toggle="modal">NBN Fibre upgrades</a>.')}</p>
        `;
                }
                if (planId !== 'SBCHRG7') {
                    upgradeAvailability.innerHTML = Drupal.t('Plus, here’s some exciting news, your address qualifies for a complementary upgrade to Fibre to the Premise (FTTP) on selected plans. Find out more about <a href="#" data-bs-target="#nbn-upgrades" data-bs-toggle="modal">NBN Fibre upgrades</a>.');
                }
            }).catch(console.error);
            selectedPlanNext.onclick = selectedPlanNextOnClick;
        }
        else {
            // Plan is not available at address.
            badges.push(Drupal.t('Not available at the address'));
            const onePlatformData = AppState.get('onePlatformData');
            addressCheckProgress.style.display = 'none';
            serviceAvailable.classList.add('p-lg-4', 'address-tile');
            serviceAvailable.querySelector('.product-unavailable--text').style.display = 'block';
            context.querySelector('#product-unavailable-icon').style.display = 'block';
            // Update more info & learn more modals with matching Plan details.
            serviceSelected.innerHTML = '';
            let selectedPlanData = yield onePlatformGetNodePlanData(actionObjectId, planType);
            onePlatformBuildPlanDisplay(actionObjectId, selectedPlanData, badges, 0, 0, false, serviceSelected);
            serviceSelected.classList.remove('selected');
            serviceSelected.classList.add('disabled');
            selectDifferentPlanLink.style.display = 'none';
            const upgradeMessages = context.querySelector('#upgrade-messages');
            upgradeMessages.innerHTML = `
        <p class="address-tile--cta">${Drupal.t('Available plans at your address')}</p>
        <p class="address-tile--text">${Drupal.t('Check out our available plans below and find the perfect fit for your needs.')}</p>
      `;
            const confirmationUpgradeBlock = context.querySelector('#address-confirmation-5');
            confirmationUpgradeBlock.innerHTML = `<p>Great! ${getMappedAccessType(accessType)} is available.</p>`;
            confirmationUpgradeBlock.appendChild(addressConfirmed);
            confirmationUpgradeBlock.style.display = 'block';
            upgradeMessages.appendChild(confirmationUpgradeBlock);
            /**
             * Asynchronously process the available plans.
             *
             * @async
             * @function processPlans
             * @returns {Promise<void>} - A promise that resolves when the processing is complete.
             */
            const processWirelessPlans = () => __awaiter(void 0, void 0, void 0, function* () {
                for (const [i, availablePlan] of availablePlansRpcIDs.entries()) {
                    const planData = yield onePlatformGetNodePlanData(availablePlan.value, planType, accessType);
                    const responseData = planData['data'];
                    const attributes = responseData && extractValidAttributes(responseData);
                    if (!attributes) {
                        continue;
                    }
                    const availablePlanId = attributes.field_plan_id;
                    const badges = [Drupal.t('Available')];
                    const affiliatePromoCode = getPromoCodeFromAffiliate();
                    let promoCode;
                    promoCode = affiliatePromoCode !== null ? affiliatePromoCode : yield getPromoCodeByLocid(onePlatformData);
                    const discount = yield handlePromotions(promoCode, availablePlans[i], badges, planData);
                    let priceDiscount = (typeof discount !== 'boolean') ? discount.promo_price : 0;
                    onePlatformBuildPlanDisplay(availablePlanId, planData, badges, 0, priceDiscount, discount ? discount : false, null);
                }
            });
            processWirelessPlans().catch(console.error);
            selectedPlanNext.onclick = selectedPlanNextOnClick;
        }
        /**
         * Stamp promo code and send to sales funnel for a given quote and action object ID.
         *
         * @param {Quote} quote - The quote object.
         * @param {string} actionObjectId - The ID of the action object.
         * @return {Promise<void>} - A Promise that resolves when the process is complete.
         */
        const stampPromoAndSendToFunnel = (quote, actionObjectId) => __awaiter(void 0, void 0, void 0, function* () {
            const validPromoData = AppState.get('onePlatformData.validPromoData');
            const promoCode = AppState.get('onePlatformData.promoCode');
            const isPromoCodeAndValidDataExists = promoCode && validPromoData && typeof validPromoData !== 'boolean';
            if (isPromoCodeAndValidDataExists) {
                const quoteItemId = quote.followedRequest.quoteItemId;
                yield onePlatformStampPromo(quote.id, actionObjectId, promoCode, quoteItemId, validPromoData);
            }
            onePlatformSendToSalesFunnel(quote.id, token);
        });
    }
});
export default onePlatformGetServicesAvailable;
